import kebabCase from 'lodash.kebabcase'

/**
 * Returns a callable function with the captured block name that consumers can
 * use to generate BEM string values with or without modifiers.
 *
 * ```js
 * const bem = bemBlock('my-element')
 * bem('save', { show: true }) => 'my-element__save my-element__save--show'
 * ```
 *
 * @param {string} block BEM block name
 * @return {Function} Callable function that generates BEM string values based
 *  on the specified block name.
 */
export function bemBlock (block) {
  if (typeof block !== 'string' || !block) {
    throw new Error('Required BEM block is missing or empty.')
  }

  return bem.bind(block.toLowerCase())
}

/**
 * Return function that consumers will use to generate BEM string values.
 * Method may be called with or without an element name as the first argument
 * followed by an additional argument for modifiers.
 * @param {string|Object} elementOrModifiers BEM element name if the type is a
 *  string. Otherwise, assumed to hold modifiers.
 * @param {Array|Object} modifiers List or object hash of modifier names to
 *  boolean flags to be add onto the string value. In list form, each modifier
 *  is assumed to be enabled. In object form, the boolean value determines
 *  whether it is included. This lets consumers dynamically flip modifiers
 *  on/off based on a variable.
 * @return {string} e.g. `block__element block__element--modifier`
 */
function bem (elementOrModifiers = {}, modifiers = {}) {
  // Block name must be bounded as context to this function.
  const block = this

  // Check if first argument has an element name.
  const element = typeof elementOrModifiers === 'string'
    ? elementOrModifiers
    : ''

  // Check if first argument is a modifier.
  modifiers = typeof elementOrModifiers === 'object'
    ? elementOrModifiers
    : modifiers

  // Ensure modifier has correct type without throwing any errors.
  modifiers = typeof modifiers === 'object'
    ? modifiers
    : {}

  const base = element ? `${block}__${element.toLowerCase()}` : block
  const value = [base]

  // Add modifiers to the return value, which may come in the form of an
  // Array or an Object hash.
  const modifierNames = modifiers.length
    ? modifiers.map((modifier) => String(modifier))
    : Object.entries(modifiers)
      .filter(([_, enabled]) => enabled)
      .map(([modifier]) => modifier)

  return value
    .concat(modifierNames.map((modifier) => `${base}--${kebabCase(modifier)}`))
    .join(' ')
}
